import { useState, useEffect } from 'react'
import { TrendingDown, AlertTriangle, CheckCircle, BarChart3, ExternalLink } from 'lucide-react'
import { generateSaasChurnDemo } from '@/lib/churn-validation/generate-demo'
import { runStatedVsActual, generateHeadline } from '@/lib/churn-validation/StatedVsActualPass'
import { formatBenchmarkComparison } from '@/lib/churn-validation/benchmarks'
import type { AttributionGap } from '@/lib/churn-validation/StatedVsActualPass'

interface Props {
  projectId: string
  runId?: string
  demo?: boolean
}

function gapColor(gap: number): string {
  const abs = Math.abs(gap)
  if (abs > 0.15) return 'text-crimson-earth'
  if (abs > 0.08) return 'text-earth-amber'
  return 'text-sage-medium'
}

function gapBarWidth(gap: number, maxGap: number): string {
  return `${Math.round(Math.abs(gap) / Math.max(maxGap, 0.01) * 100)}%`
}

export function ChurnValidationPage({ projectId, runId, demo = false }: Props) {
  const [gaps, setGaps] = useState<AttributionGap[]>([])
  const [headline, setHeadline] = useState('')
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        if (demo || !runId) {
          // Use seeded demo data
          const { customers } = generateSaasChurnDemo()
          const result = runStatedVsActual(customers)
          setGaps(result)
          setHeadline(generateHeadline(result))
        } else {
          const token = localStorage.getItem('echo_session')
          const headers: Record<string, string> = {}
          if (token) headers['Authorization'] = `Bearer ${token}`
          const res = await fetch(`/api/v1/projects/${projectId}/churn-validation/latest`, { headers })
          if (res.ok) {
            const data = await res.json() as { result_json?: string }
            if (data.result_json) {
              const result = JSON.parse(data.result_json) as AttributionGap[]
              setGaps(result)
              setHeadline(generateHeadline(result))
            }
          }
        }
      } catch {
        // Fall back to demo data
        const { customers } = generateSaasChurnDemo()
        const result = runStatedVsActual(customers)
        setGaps(result)
        setHeadline(generateHeadline(result))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [projectId, runId, demo])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground text-sm">Analysing churn attribution...</div>
      </div>
    )
  }

  const maxGap = gaps.length > 0 ? Math.max(...gaps.map(g => Math.abs(g.gap))) : 0

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Headline card */}
      <div className="rounded-xl border border-border bg-surface-subtle p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-earth-amber mt-0.5" size={22} strokeWidth={1.75} />
          <div>
            <p className="text-lg font-semibold text-foreground">{headline}</p>
            {demo && (
              <p className="text-xs text-muted-foreground mt-1">
                Demo data · 247 churned customers · Mixpanel + Stripe signals
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Attribution gap table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-foreground text-sm">Stated vs. Validated Attribution</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Positive gap = stated reason overstates behavior · Negative gap = understated
          </p>
        </div>
        <div className="divide-y divide-border">
          {gaps.map(gap => (
            <div key={gap.stated_reason}>
              <div
                className="p-4 cursor-pointer hover:bg-surface-subtle/50 transition-colors"
                onClick={() => setExpanded(expanded === gap.stated_reason ? null : gap.stated_reason)}
              >
                <div className="grid grid-cols-4 gap-4 items-center">
                  <div>
                    <p className="text-sm font-medium text-foreground">{gap.stated_reason}</p>
                    <p className="text-xs text-muted-foreground">{gap.stated_n} stated</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Stated</p>
                    <p className="text-lg font-bold text-foreground">{Math.round(gap.stated_share * 100)}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Validated</p>
                    <p className="text-lg font-bold text-foreground">{Math.round(gap.validated_share * 100)}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Gap</p>
                    <p className={`text-lg font-bold ${gapColor(gap.gap)}`}>
                      {gap.gap > 0 ? '+' : ''}{Math.round(gap.gap * 100)}pp
                    </p>
                    <div className="h-1 bg-border rounded mt-1 overflow-hidden">
                      <div
                        className={`h-full rounded ${gap.gap < -0.08 ? 'bg-crimson-earth' : gap.gap < -0.03 ? 'bg-earth-amber' : 'bg-sage-medium'}`}
                        style={{ width: gapBarWidth(gap.gap, maxGap) }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {expanded === gap.stated_reason && (
                <div className="px-4 pb-4 bg-surface-subtle/30">
                  <div className="grid grid-cols-2 gap-6 mt-3">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                        Actual behavioral drivers
                      </p>
                      <div className="space-y-1.5">
                        {(gap.actual_drivers ?? []).map(d => (
                          <div key={d.driver} className="flex items-center gap-2">
                            <div className="h-1.5 bg-echo-indigo/20 rounded flex-1 overflow-hidden">
                              <div
                                className="h-full bg-echo-indigo rounded"
                                style={{ width: `${Math.round(d.share * 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground w-24 shrink-0">{d.driver}</span>
                            <span className="text-xs font-medium text-foreground w-8 text-right">
                              {Math.round(d.share * 100)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                        Evidence citations
                      </p>
                      <div className="space-y-2">
                        {(gap.evidence_citations ?? []).slice(0, 2).map((cite, idx) => (
                          <div key={idx} className="text-xs border border-border rounded p-2 bg-white">
                            <p className="text-muted-foreground">Stated: {cite.stated}</p>
                            <p className="text-foreground mt-0.5">{cite.behavior_signature}</p>
                            <p className="text-muted-foreground/60 mt-0.5">ID: {cite.customer_id_hash}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Wilson confidence band */}
                  <p className="text-xs text-muted-foreground mt-3">
                    Validated share confidence band: {Array.isArray(gap.confidence_band) && gap.confidence_band.length === 2
                      ? `${Math.round(gap.confidence_band[0] * 100)}%–${Math.round(gap.confidence_band[1] * 100)}% (95% Wilson)`
                      : 'N/A'}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Benchmark comparison */}
      <div className="rounded-xl border border-border p-5">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 size={18} strokeWidth={1.75} className="text-echo-indigo" />
          <h3 className="font-semibold text-sm text-foreground">SaaS Benchmark Comparison</h3>
        </div>
        <div className="space-y-2">
          {gaps.slice(0, 3).map(gap => {
            const reasonKey = gap.stated_reason.toLowerCase().includes('price') ? 'price'
              : gap.stated_reason.toLowerCase().includes('feature') ? 'features' : 'support'
            const comparison = formatBenchmarkComparison(gap.gap, reasonKey)
            return (
              <p key={gap.stated_reason} className="text-sm text-muted-foreground">{comparison}</p>
            )
          })}
        </div>
      </div>

      {/* Recommended actions */}
      <div className="rounded-xl border border-border p-5">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle size={18} strokeWidth={1.75} className="text-sage-medium" />
          <h3 className="font-semibold text-sm text-foreground">Recommended Actions</h3>
        </div>
        <div className="space-y-2">
          {gaps.slice(0, 3).map((gap, i) => {
            const topDriver = gap.actual_drivers[0]?.driver ?? 'unknown'
            let action = ''
            if (topDriver === 'Activation failure') {
              action = `Re-evaluate retention spend allocated to "${gap.stated_reason}"-driven interventions — ${Math.round(Math.abs(gap.gap) * 100)}pp of your budget may be targeting the wrong root cause. Focus on activation and feature adoption instead.`
            } else if (topDriver === 'Engagement decay') {
              action = `"${gap.stated_reason}" churn is largely driven by engagement decay — invest in proactive health scoring and in-app nudges rather than pricing adjustments.`
            } else {
              action = `Investigate "${gap.stated_reason}" attribution more closely — behavioral signals diverge from stated reasons by ${Math.round(Math.abs(gap.gap) * 100)}pp.`
            }
            return (
              <div key={i} className="flex gap-2 text-sm">
                <span className="text-echo-indigo shrink-0">{i + 1}.</span>
                <p className="text-foreground">{action}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Export */}
      <div className="flex gap-3">
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-lg hover:bg-surface-subtle transition-colors"
        >
          <ExternalLink size={14} strokeWidth={1.75} />
          Export PDF
        </button>
        <button
          onClick={() => {
            const csv = gaps.map(g => `"${g.stated_reason}",${g.stated_share},${g.validated_share},${g.gap}`).join('\n')
            const blob = new Blob([`stated_reason,stated_share,validated_share,gap\n${csv}`], { type: 'text/csv' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'churn-attribution-gaps.csv'
            a.click()
          }}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-lg hover:bg-surface-subtle transition-colors"
        >
          <TrendingDown size={14} strokeWidth={1.75} />
          Export CSV
        </button>
      </div>
    </div>
  )
}

export default ChurnValidationPage
